import React, { useEffect, useState } from "react";
import {
  Building2,
  Search,
  AlertCircle,
  ShieldAlert,
  FileSearch,
  UserSearch,
  Loader,
  ListCheck,
  FileJson,
  Copy,
  Loader2,
} from "lucide-react";
import {
  CheckCircle2,
  ExternalLink,
  AlertTriangle,
  Scale,
  MapPin,
  User,
  FileText,
  Fingerprint,
  Activity,
  Gavel,
  Calendar,
  Users,
  ShieldCheck,
  Hash,
} from "lucide-react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { useService } from "../../../context/ServiceContext";
import { fetchProfile } from "../../../Services/profile/profileservice";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { verifyFIR } from "../../../Services/ApiTestService/apiTestService";

const CheckFIR = () => {
  const { serviceName } = useService();
  const [isLoading, setIsLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);

  const [searchType, setSearchType] = useState("individual");
  const [formData, setFormData] = useState({
    name: "",
    fatherName: "",
    directors: "",
  });

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied!"));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      api_id: profileData.user_id,
      api_key: profileData.api_key,
      token_id: profileData.token_id,
      search_type: searchType,
    };

    if (searchType === "individual") {
      setFormData((prev) => ({ ...prev, directors: "" }));
    } else {
      setFormData((prev) => ({ ...prev, fatherName: "" }));
    }
    if (formData?.name) payload.name = formData?.name;
    if (formData?.directors) payload.directors = formData?.directors;
    if (formData?.fatherName) payload.father_name = formData?.fatherName;

    console.log("Initiating FIR Admin Check:", searchType, formData);

    setIsLoading(true);

    try {
      const res = await verifyFIR(payload);

      if (res) {
        console.log("res data FIR", res);
      }

      if (res?.status?.code === 200) {
        setResponseData(res);
        showSuccessToast(
          res?.data?.message || res?.message || res?.data?.data?.message,
        );
      } else {
        showErrorToast(
          res?.data?.message || res?.message || res?.data?.data?.message,
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);


  const validform = formData?.name && searchType === "individual" ? formData?.fatherName : formData?.directors 

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}
      <div className="min-h-screen w-full bg-slate-50 flex flex-col items-center justify-center p-4 pt-2">
        {/* Clean Admin Panel Card */}
        <div className="w-full max-w-2xl bg-white border border-slate-200 shadow-sm rounded-lg p-5">
          {/* Compact Header */}
          <div className="flex items-center gap-2 mb-5 border-b border-slate-100 pb-3">
            <div className="bg-blue-50 p-1.5 rounded-md text-blue-600">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">
                FIR Record Search
              </h2>
              <p className="text-sm text-slate-500">
                Query official registries
              </p>
            </div>
          </div>

          {/* High-Density Tabs */}
          <div className="flex p-1 mb-5 bg-slate-100/80 rounded-md border border-slate-200/50">
            <button
              type="button"
              onClick={() => setSearchType("individual")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded text-sm font-medium transition-colors ${
                searchType === "individual"
                  ? "bg-white text-blue-700 shadow-sm border border-slate-200/50"
                  : "text-slate-600 hover:text-slate-800 hover:bg-slate-200/50"
              }`}
            >
              <UserSearch className="w-3.5 h-3.5" />
              Individual
            </button>
            <button
              type="button"
              onClick={() => setSearchType("corporate")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded text-sm font-medium transition-colors ${
                searchType === "corporate"
                  ? "bg-white text-blue-700 shadow-sm border border-slate-200/50"
                  : "text-slate-600 hover:text-slate-800 hover:bg-slate-200/50"
              }`}
            >
              <Scale className="w-3.5 h-3.5" />
              Corporate
            </button>
          </div>

          {/* Form with Reduced Spacing */}
          <form onSubmit={handleSubmit} className="space-y-3.5">
            {/* Common Name Field */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                {searchType === "individual"
                  ? "Subject Legal Name"
                  : "Registered Entity Name"}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400">
                  {searchType === "individual" ? (
                    <UserSearch className="w-4 h-4" />
                  ) : (
                    <Building2 className="w-4 h-4" />
                  )}
                </div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full pl-8 pr-3 py-2.5 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors text-slate-800 placeholder:text-slate-400"
                  placeholder={
                    searchType === "individual"
                      ? "Enter exact legal name"
                      : "Enter registered name"
                  }
                  required
                />
              </div>
            </div>

            {/* Conditional Second Field */}
            {searchType === "individual" ? (
              <div className="animate-in fade-in duration-200">
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Father's Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400">
                    <UserSearch className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    name="fatherName"
                    value={formData.fatherName}
                    onChange={handleInputChange}
                    className="w-full pl-8 pr-3 py-2.5 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors text-slate-800 placeholder:text-slate-400"
                    placeholder="For disambiguation"
                    required
                  />
                </div>
              </div>
            ) : (
              <div className="animate-in fade-in duration-200">
                <label className="block text-sm font-semibold text-slate-700 mb-1">
                  Board of Directors
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400">
                    <FileSearch className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    name="directors"
                    value={formData.directors}
                    onChange={handleInputChange}
                    className="w-full pl-8 pr-3 py-2.5 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors text-slate-800 placeholder:text-slate-400"
                    placeholder="e.g. John Doe, Jane Smith"
                    required
                  />
                </div>
                {/* Compact UX Note */}
                <div className="flex items-start gap-1.5 mt-2 bg-red-50/50 border border-red-100 p-2 rounded text-red-800">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-red-600" />
                  <p className="text-sm leading-tight">
                    Separate multiple director names with a comma ( , ) for
                    accurate bulk querying.
                  </p>
                </div>
              </div>
            )}

            {/* Submit Button */}
        <button
        type="submit"
        disabled={!validform}
        className={`w-full mt-2 flex items-center disabled:opacity-50 disabled:cursor-not-allowed justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
            isLoading 
            ? 'bg-blue-500 cursor-not-allowed text-white ' 
            : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
        }`  }
        >
        {isLoading ? (
            <div className="flex items-center justify-center gap-1.5">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Please Wait...</span>
            </div>
        ) : (
            <div className="flex items-center justify-center gap-1.5">
            <Search className="w-4 h-4" />
            <span>Verify</span>
            </div>
        )}
        </button>
          </form>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {responseData?.status?.code === 200 && (
          <div className="grid grid-cols-1 gap-6 mt-10 w-full max-w-6xl">
            <div className="bg-gray-200 p-5 rounded-md">
              <h2 className="text-2xl flex items-center justify-center gap-2 font-bold text-blue-500 mb-1">
                <ListCheck />
                FIR Data
              </h2>
              <hr className="mb-8" />

              {/* Render Results if Success */}
              {responseData?.status?.code === 200 && responseData?.data && (
                <div className="w-full mt-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  {/* Global Meta & Success Banner */}
                  <div className="bg-white border border-emerald-200 rounded-md shadow-sm overflow-hidden mb-4">
                    <div className="bg-emerald-50 px-3 py-2 border-b border-emerald-100 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-semibold text-emerald-800">
                        {responseData.message}
                      </span>
                    </div>
                    <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-3 bg-slate-50/50">
                      <div>
                        <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">
                          Client Ref
                        </p>
                        <p className="text-xs font-mono text-slate-800">
                          {responseData.data.client_ref_num || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">
                          Request ID
                        </p>
                        <p className="text-xs font-mono text-slate-800">
                          {responseData.data.request_id || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">
                          Result Code
                        </p>
                        <p className="text-xs font-mono text-slate-800">
                          {responseData.data.result_code || "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">
                          Total Records
                        </p>
                        <p className="text-xs font-mono text-slate-800">
                          {responseData.data.cases?.district_courts?.length ||
                            0}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Section Title */}
                  <div className="mb-3 pl-1 flex items-center gap-1.5">
                    <Scale className="w-4 h-4 text-slate-700" />
                    <h3 className="text-sm font-bold text-slate-800">
                      District Court Records
                    </h3>
                  </div>

                  {/* Cases List */}
                  <div className="space-y-4">
                    {responseData.data.cases?.district_courts?.map(
                      (record, index) => {
                        const {
                          personal_details,
                          match_details,
                          case_details,
                          risk_details,
                        } = record;
                        const isHighRisk =
                          risk_details?.case_risk === "high risk";

                        const renderField = (label, value) => (
                          <div className="flex flex-col mb-1.5">
                            <span className="text-[10px] text-slate-500 font-medium leading-tight">
                              {label}
                            </span>
                            <span className="text-[11px] text-slate-800 font-medium leading-tight break-words">
                              {value && value !== "---" ? (
                                value
                              ) : (
                                <span className="text-slate-400">N/A</span>
                              )}
                            </span>
                          </div>
                        );

                        return (
                          <div
                            key={index}
                            className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden"
                          >
                            {/* Card Header (Category, Risk & IDs) */}
                            <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 flex flex-wrap justify-between items-center gap-2">
                              <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-slate-700">
                                <span className="uppercase tracking-wide text-blue-700">
                                  {case_details?.case_category || "Unknown"}
                                </span>
                                <span className="flex items-center gap-1 text-slate-500">
                                  <Calendar className="w-3.5 h-3.5" />{" "}
                                  {case_details?.case_year || "N/A"}
                                </span>
                                <span className="flex items-center gap-1 text-slate-500">
                                  <Hash className="w-3.5 h-3.5" />{" "}
                                  {case_details?.cnr ||
                                    case_details?.case_id ||
                                    "No ID"}
                                </span>
                                {case_details?.case_status && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-200 text-slate-600 uppercase tracking-wide">
                                    {case_details.case_status}
                                  </span>
                                )}
                              </div>

                              <div
                                className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold uppercase tracking-wider ${
                                  isHighRisk
                                    ? "bg-red-50 text-red-700 border border-red-200"
                                    : "bg-orange-50 text-orange-700 border border-orange-200"
                                }`}
                              >
                                <AlertTriangle className="w-3 h-3" />
                                {risk_details?.case_risk}
                              </div>
                            </div>

                            {/* Comprehensive Grid Data */}
                            <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-4 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                              {/* Column 1: Match & Risk Analysis */}
                              <div className="space-y-3">
                                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 border-b border-slate-100 pb-1.5">
                                  <Activity className="w-3.5 h-3.5 text-blue-600" />{" "}
                                  Scoring Metrics
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="col-span-2 bg-blue-50 border border-blue-100 rounded p-1.5 flex justify-between items-center">
                                    <span className="text-[10px] font-bold text-blue-800 uppercase tracking-wide">
                                      Final Confidence
                                    </span>
                                    <span className="text-xs font-bold text-blue-700">
                                      {match_details?.final_score}%
                                    </span>
                                  </div>
                                  {renderField(
                                    "Name Score",
                                    `${match_details?.name_score || 0}% (${match_details?.name_match_type})`,
                                  )}
                                  {renderField(
                                    "Address Score",
                                    `${match_details?.address_score || 0}%`,
                                  )}
                                  {renderField(
                                    "Father Score",
                                    `${match_details?.father_score || "0"}% (${match_details?.father_match_type})`,
                                  )}
                                  {renderField(
                                    "Jurisdiction",
                                    `${case_details?.jurisdiction_type} ${case_details?.jurisdiction_match ? "(Matched)" : "(No Match)"}`,
                                  )}
                                </div>
                              </div>

                              {/* Column 2: Subject Details */}
                              <div className="space-y-3 pt-3 md:pt-0 md:pl-4">
                                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 border-b border-slate-100 pb-1.5">
                                  <User className="w-3.5 h-3.5 text-blue-600" />{" "}
                                  Subject Identity
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="col-span-2">
                                    {renderField(
                                      "Primary Name",
                                      personal_details?.name,
                                    )}
                                  </div>
                                  {renderField(
                                    "Father's Name",
                                    personal_details?.father_name,
                                  )}
                                  {renderField(
                                    "Case Role",
                                    personal_details?.role,
                                  )}

                                  <div className="col-span-2">
                                    {renderField(
                                      "Address",
                                      personal_details?.address,
                                    )}
                                  </div>

                                  {/* Corporate specific fields */}
                                  {(personal_details?.director_match ||
                                    personal_details?.director_matches) && (
                                    <div className="col-span-2 border-t border-slate-100 pt-2 mt-1">
                                      {renderField(
                                        "Director Match",
                                        personal_details?.director_match,
                                      )}
                                      {renderField(
                                        "Director Matches",
                                        personal_details?.director_matches,
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Column 3: Legal & Court Specifics */}
                              <div className="space-y-3 pt-3 md:pt-0 md:pl-4">
                                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 border-b border-slate-100 pb-1.5">
                                  <Gavel className="w-3.5 h-3.5 text-blue-600" />{" "}
                                  Case Specifics
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="col-span-2">
                                    {renderField(
                                      "Court Name",
                                      case_details?.court_name,
                                    )}
                                  </div>
                                  {renderField(
                                    "Acts & Sections",
                                    case_details?.acts
                                      ? `${case_details.acts} / Sec: ${case_details.sections}`
                                      : null,
                                  )}
                                  {renderField(
                                    "FIR Number",
                                    case_details?.fir_number,
                                  )}
                                  {renderField(
                                    "Case Filter / Subtype",
                                    `${case_details?.case_filter || "N/A"} ${case_details?.subtype ? `/ ${case_details.subtype}` : ""}`,
                                  )}
                                  {renderField(
                                    "Nature of Disposal",
                                    case_details?.nature_of_disposal,
                                  )}

                                  <div className="col-span-2 mt-1 pt-2 border-t border-slate-100">
                                    <span className="text-[10px] text-slate-500 font-medium mb-1 block flex items-center gap-1">
                                      <Users className="w-3 h-3" /> Other
                                      Parties involved
                                    </span>
                                    <p className="text-[11px] text-slate-800 font-medium">
                                      {case_details?.other_parties || "N/A"}
                                      {case_details?.other_party_role &&
                                        ` (${case_details.other_party_role})`}
                                    </p>
                                  </div>

                                  {/* Dates */}
                                  {(case_details?.case_decision_date ||
                                    case_details?.next_hearing_date) && (
                                    <div className="col-span-2 grid grid-cols-2 gap-2 mt-1 pt-2 border-t border-slate-100">
                                      {renderField(
                                        "Decision Date",
                                        case_details?.case_decision_date,
                                      )}
                                      {renderField(
                                        "Next Hearing",
                                        case_details?.next_hearing_date,
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Card Footer (Action Link & Tech IDs) */}
                            <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 flex justify-between items-center flex-wrap gap-2">
                              <span className="text-[9px] text-slate-400 font-mono tracking-wider flex items-center gap-1">
                                <Fingerprint className="w-3 h-3" /> Case ID:{" "}
                                {case_details?.case_id || "N/A"}
                              </span>

                              {case_details?.case_link && (
                                <a
                                  href={case_details.case_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-600 hover:text-blue-800 hover:underline transition-colors bg-blue-100/50 px-2 py-1 rounded"
                                >
                                  Verify Original Source{" "}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(responseData)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON responseData
              </p>
              <hr className="mb-8" />{" "}
              <pre className="text-[16px]">
                {JSON.stringify(responseData, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CheckFIR;
